import { useMutation, useQueryClient } from '@tanstack/react-query'
import { fatal } from '@/utils'
import { IBCTransferStatus, traceIbcStatus2 } from '@/wallet-utils'
import { queryKeys } from '@/query-keys'
import { MutationParameters } from './types'

// [sign/broadcast] mutations and [trace] mutation are a 2-part process.
// First, if [sign/broadcast] doesn't succeed within the UI time limit, [trace] mutation will continue
// the [tracing] process so user can continue when the ibc transfer completes if they haven't closed the modal yet.
// We can use the `isLoading` status provided by [trace] mutation to display the "Return Later" screen.
const useTraceIbcTransfer = ({ withdrawStTokenTransaction }: MutationParameters) => {
  const client = useQueryClient()

  return useMutation({
    mutationFn: async (): Promise<{ status: IBCTransferStatus }> => {
      if (!withdrawStTokenTransaction) {
        // We expect this to be set by the [sign/broadcast] mutation.
        // If this is empty, it's likely that modal close handler reset the ref.
        // And that we forgot to check if the modal is still open when we ran this mutation.
        throw fatal('Unable to trace ibc status of stEVMOS while transaction is empty.')
      }

      // @TODO: Handle timeout state if this doesn't succeed. The original implementation
      // from the ibc/staking modal doesn't have this yet neither. Add display states for
      const status = await traceIbcStatus2({ denom: 'STRD', tx: withdrawStTokenTransaction }, { timeout: false })

      await client.invalidateQueries({
        queryKey: queryKeys.chainBalancesBase
      })

      await client.invalidateQueries({
        queryKey: queryKeys.transactionHistoryLsmIbcBalancesBase
      })

      await client.invalidateQueries({
        queryKey: queryKeys.transactionHistoryIbcStatusByHash({ hash: withdrawStTokenTransaction.transactionHash })
      })

      return { status }
    }
  })
}

export { useTraceIbcTransfer }
